// Import necessary dependencies and components
import React, { useState, useMemo, useCallback } from "react";
import useCustomQuery from "../../../Hooks/useCustomQuery";
import usePost from "../../../Hooks/usePost";
import Communities from "./Communities";
import AllEventPersonnelItems from "./AllEventPersonnelItems";
import AllAttendeeEvents from "./AllAttendeeEvents";
import Nothing from "../../../components/Loader/Contacts";
import PropTypes from "prop-types";

function AllEvents({ 
  search, 
  userId, 
  userEmail, 
  token, 
  t, 
  isSwitchOn,
  showArchives = false
}) {
  const [restoringEventId, setRestoringEventId] = useState(null);
  const { execute: restoreEvent, pending: isRestoring } = usePost();

  // ✅ Fetch personnel events based on showArchives state
  const {
    data: getEvents,
    error: errorGetEvents,
    isLoading: isLoadingGetEvents,
    refetch: refetchEvents
  } = useCustomQuery(
    ["MyEvents", userEmail, showArchives], // ✅ Cache isolation via query key
    showArchives
      ? `${process.env.REACT_APP_API}events/deleted`
      : `${process.env.REACT_APP_API}events/personnel?email=${userEmail}`,
    token,
    { enabled: !!userEmail }
  );

  // ✅ Fetch attendee events with showArchives support
  const {
    data: getEventsAsAttendee,
    error: errorGetEventsAsAttendee,
    isLoading: isLoadingGetEventsAsAttendee,
    refetch: refetchAttendeeEvents
  } = useCustomQuery(
    ["MyEventsAsAttendee", userEmail, showArchives], // ✅ Include showArchives in key
    `${process.env.REACT_APP_API}events/attendee-events?email=${userEmail}`,
    token,
    { enabled: !!userEmail }
  );

  // ✅ Handle restore event (works for both personnel and attendee events)
  const handleRestore = async (eventId) => {
    if (!window.confirm("Are you sure you want to restore this event?")) {
      return;
    }

    setRestoringEventId(eventId);
    try {
      await restoreEvent(
        `events/restore/${eventId}`,
        null,
        "PATCH",
        "Event restored successfully",
        token,
        true
      );
      // ✅ Refetch both queries to ensure UI sync after restore
      await refetchEvents();
      await refetchAttendeeEvents();
    } catch (error) {
      console.error("Failed to restore event:", error);
    } finally {
      setRestoringEventId(null);
    }
  };

  // ✅ Simplified search filter (memoized to prevent recreations)
  const filterEventsByName = useCallback((events, searchQuery) => {
    if (!events) return [];
    if (!searchQuery && !isSwitchOn) return [];
    
    const query = searchQuery.toLowerCase();
    return events.filter(event =>
      event?.eventName?.toLowerCase().includes(query) ||
      event?.eventTitle?.toLowerCase().includes(query)
    );
  }, [isSwitchOn]);

  // ✅ Safety filter: guarantee archive/active separation at client level
  const filterByArchiveStatus = useCallback((events, isArchivedView) => {
    if (!events) return [];
    return events.filter(event => {
      const isArchived = !!event.deletedAt;
      return isArchivedView ? isArchived : !isArchived;
    });
  }, []);

  // ✅ Apply search + archive filters with useMemo for performance
  const filteredEvents = useMemo(() => {
    const searched = filterEventsByName(getEvents?.data, search);
    return filterByArchiveStatus(searched, showArchives);
  }, [getEvents?.data, search, showArchives, filterEventsByName, filterByArchiveStatus]);

  const filteredAttendeEvents = useMemo(() => {
    const searched = filterEventsByName(getEventsAsAttendee?.data, search);
    return filterByArchiveStatus(searched, showArchives);
  }, [getEventsAsAttendee?.data, search, showArchives, filterEventsByName, filterByArchiveStatus]);

  const isViewEvent = isSwitchOn || search;

  // ✅ Improved empty state logic
  const isEmptyPersonnel = !isLoadingGetEvents && 
    ((!showArchives && getEvents?.data?.length === 0) ||
     (showArchives && (!getEvents?.data || getEvents?.data?.length === 0)));

  const isEmptyAttendee = !isLoadingGetEventsAsAttendee &&
    (!filteredAttendeEvents || filteredAttendeEvents.length === 0);

  const isEmptyEvent = isEmptyPersonnel && isEmptyAttendee;

  return (
    <>
      {!isViewEvent ? (
        <Communities token={token} userEmail={userEmail} />
      ) : (
        <>
          {isEmptyEvent ? (
            <div className="h1">
              <Nothing 
                title={showArchives 
                  ? "No archived events found" 
                  : "You have no event at the moment"} 
                subtitle={showArchives 
                  ? "Events you delete will appear here" 
                  : "Create your first event to get started"}
              />
            </div>
          ) : (
            <>
              {/* ✅ Pass all required props to AllAttendeeEvents */}
              <AllAttendeeEvents
                userEmail={userEmail}
                filteredAttendeEvents={filteredAttendeEvents}
                isLoadingGetEvents={isLoadingGetEventsAsAttendee}
                showArchives={showArchives}
                onRestore={handleRestore}
                restoringEventId={restoringEventId}
                isRestoring={isRestoring}
              />
              
              {/* ✅ Pass all required props to AllEventPersonnelItems */}
              <AllEventPersonnelItems
                userEmail={userEmail}
                filteredEvents={filteredEvents}
                isLoadingGetEvents={isLoadingGetEvents}
                isPersonnelEmpty={isEmptyPersonnel}
                showArchives={showArchives}
                onRestore={handleRestore}
                restoringEventId={restoringEventId}
                isRestoring={isRestoring}
              />
            </>
          )}
        </>
      )}
    </>
  );
}

// ✅ PropTypes with proper validation
AllEvents.propTypes = {
  search: PropTypes.string.isRequired,
  userId: PropTypes.string,
  userEmail: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  isSwitchOn: PropTypes.bool.isRequired,
  showArchives: PropTypes.bool.isRequired,
};

export default AllEvents;